import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { get } from '~service/shop-service';
import { DetailProduct, ProductConfiguration, ProductConfigurator, ProductConfiguratorOption, ProductVariant } from '~model/product';
import { translate } from '~service/translation-service';

type ChangeSet = { [groupNumber: number]: number };

@Component({
  tag: 'uvex-article-detail',
  styleUrl: './article-detail.scss',
})
export class ArticleDetail {
  @Element() el: HTMLElement;
  @Prop() url: string;
  @Prop() ceDeclarationsUrl: string;
  @Prop() shopUrl: string;
  @Prop() ccToken: string;
  @Prop() ccWidgetid: string;
  @Prop() ccLocale: string;
  @Prop() ccDisplayLanguage: string;
  @Prop() ccSubid: string;
  @Prop() ccShowWidget: number;
  @Prop() ccAllowedCountries: string;
  @State() product: DetailProduct;
  @State() orderNumber: string;
  @State() variant: ProductVariant = null;
  @State() selected?: ProductConfiguration = null;

  async componentWillLoad() {
    try {
      this.product = await get(this.url);
      this.orderNumber = this.product.mainOrdernumber;
      this.variant = this.product.variants[this.orderNumber];
      this.setPreselectedOptions();

      if (this.product.configurator.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('number', this.orderNumber);
        window.history.replaceState(this.orderNumber, this.product.name, `?${urlParams.toString()}`);
      }
    } catch (e) {}
  }

  sortOptions(configurator: ProductConfigurator) {
    let sortableOptions = [];
    for (let option in configurator.options) {
      sortableOptions.push(configurator.options[option]);
    }

    sortableOptions.sort((a: ProductConfiguratorOption, b: ProductConfiguratorOption) => {
      return a.position - b.position;
    });

    return sortableOptions;
  }

  setPreselectedOptions() {
    Object.keys(this.product.configurations).forEach(configurationIdentifier => {
      const configuration = this.product.configurations[configurationIdentifier];
      if (configuration.isPreselected) {
        this.selected = configuration;
      }
    });
  }

  findChangedProductConfiguration(change: ChangeSet): ProductConfiguration | null {
    const changed = Object.assign({}, this.selected.configuration, change);
    const changedHash = Object.entries(changed).toString();
    for (let key in this.product.configurations) {
      let existingHash = Object.entries(this.product.configurations[key].configuration).toString();
      if (existingHash === changedHash) {
        return this.product.configurations[key];
      }
    }
    return null;
  }

  canBeApplied(change: ChangeSet): boolean {
    const partiallyMatching = this.findChangedProductConfiguration(change);
    return partiallyMatching !== null;
  }

  getNextGroupNumberThatCouldBeChangedAsWell(change: ChangeSet): number | null {
    const allGroupNumbers = this.product.configurator.map(configurator => configurator.groupId);
    const currentOffsets = Object.keys(change).map(key => allGroupNumbers.indexOf(parseInt(`${key}`, 10)));
    const currentOffset = Math.max(...currentOffsets);
    if (currentOffset === allGroupNumbers.length - 1) {
      return null;
    }
    return allGroupNumbers[currentOffset + 1];
  }

  getAllValuesForGroupNumber(groupNumber: number): number[] {
    const group = this.product.configurator.filter(configurator => configurator.groupId == groupNumber).shift();
    return Object.keys(group.options).map(key => parseInt(`${key}`));
  }

  isChangeEnabledForVariant(change: ChangeSet): boolean {
    return this.findBestMatchingChangeSet(change) !== null;
  }

  findBestMatchingChangeSet(change: ChangeSet): ChangeSet | null {
    if (this.canBeApplied(change)) {
      return change;
    }

    const nextGroupNumberWeCouldChangeAsWell = this.getNextGroupNumberThatCouldBeChangedAsWell(change);
    if (nextGroupNumberWeCouldChangeAsWell === null) {
      return null;
    }
    const allValuesForGroupNumber = this.getAllValuesForGroupNumber(nextGroupNumberWeCouldChangeAsWell);
    for (let key in allValuesForGroupNumber) {
      change[nextGroupNumberWeCouldChangeAsWell] = allValuesForGroupNumber[key];
      const bestMatching = this.findBestMatchingChangeSet(change);
      if (bestMatching) {
        return bestMatching;
      }
    }
    return null;
  }

  isOptionDisabledForVariant(option: ProductConfiguratorOption, groupNumber?: number): boolean {
    let change = {};
    change[groupNumber] = option.id;
    return !this.isChangeEnabledForVariant(change);
  }

  onVariantOptionSelection(event, option: ProductConfiguratorOption) {
    event.preventDefault();

    let change = {};
    change[this.getGroupIdOfOption(option)] = option.id;
    const changed = this.findBestMatchingChangeSet(change);
    const configuration = this.findChangedProductConfiguration(changed);
    if (configuration) {
      this.selected = configuration;
      this.orderNumber = configuration.ordernumber;
      this.variant = this.product.variants[this.orderNumber];
    }

    window.history.replaceState(this.orderNumber, this.product.name, '?number=' + this.orderNumber);
  }

  getGroupIdOfOption(selectedOption: ProductConfiguratorOption) {
    let groupId = null;
    this.product.configurator.forEach((configurator, key) => {
      Object.keys(configurator.options).map(optionKey => {
        if (selectedOption.id === this.product.configurator[key].options[optionKey].id) {
          groupId = this.product.configurator[key].groupId;
        }
      });
    });
    return groupId;
  }

  renderImages() {
    const images = [this.variant.mainImage, ...Object.values(this.variant.images)];
    return <uvex-product-images images={images}>{this.renderCo2Wolke()}</uvex-product-images>;
  }

  renderDetails() {
    return (
      <div class="product-details">
        {this.product.shortDescription ? <div class="product-short-description" innerHTML={this.product.shortDescription} /> : this.renderInfo()}
        {this.renderPrice()}
        {this.renderConfigurator()}
        <div class="product-buybox-container">
          {this.renderBuyBox()}
          {this.renderDealerLocator()}
        </div>
        {this.renderCommerceConnector()}
      </div>
    );
  }

  renderPrice() {
    if (!this.variant.price) {
      return;
    }

    return (
      <div class="product-price-box">
        <span class="product-price" innerHTML={this.variant.price.gross.formated} />
        <span class="product-tax"> ({translate('sw.article.properties.withTax')})</span>
        <div class="brutto-price product-tax" innerHTML={this.variant.price.net.formated + ' (' + translate('sw.article.properties.withoutTax') + ')'} />
      </div>
    );
  }

  renderInfo() {
    return (
      <table class="product-info unstyled">
        <tbody>
          <tr hidden={!this.variant.ordernumber}>
            <td>{translate('sw.article.properties.ordernumber')}:</td>
            <td>{this.variant.ordernumber}</td>
          </tr>
          <tr hidden={!this.variant.packUnit}>
            <td>{translate('sw.article.properties.packunit')}:</td>
            <td>
              {this.variant.purchaseUnit}&nbsp;{this.variant.packUnit}
            </td>
          </tr>
          <tr hidden={!this.variant.ean}>
            <td>{translate('sw.article.properties.ean')}:</td>
            <td>{this.variant.ean}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderConfigurator() {
    return (
      <div class="product-option-group">
        {this.product.configurator.map(configurator => {
          let selectedOptionMediaName = '';
          if (1 in this.selected.configuration) {
            Object.keys(configurator.options).map(key => {
              const option = configurator.options[key];
              if (option.media && option.id === this.selected.configuration[1]) {
                selectedOptionMediaName = option.name;
              }
            });
          }

          return (
            <div class="product-option">
              <h5 class="product-option-label">
                {translate('sw.article.properties.configuratorGroup.groupName.' + configurator.groupId)}: {selectedOptionMediaName}
              </h5>
              <div class="product-options">
                {this.sortOptions(configurator).map((configuratorOption: ProductConfiguratorOption) => {
                  const option = configurator.options[configuratorOption.id];
                  const groupId = configurator.groupId;

                  const disabled = this.isOptionDisabledForVariant(option, groupId);
                  const selected = this.selected.configuration[groupId];

                  if (option.media) {
                    return (
                      <a
                        href="#"
                        class={{
                          'product-option-item icon': true,
                          'disabled': disabled,
                          'selected': selected === option.id,
                        }}
                        onClick={event => {
                          this.onVariantOptionSelection(event, option);
                        }}
                        style={{ 'background-image': 'url( ' + option.media.thumbnail + ')' }}
                      ></a>
                    );
                  } else {
                    return (
                      <a
                        href="#"
                        class={{
                          'product-option-item': true,
                          'disabled': disabled,
                          'selected': selected === option.id,
                        }}
                        onClick={event => {
                          this.onVariantOptionSelection(event, option);
                        }}
                      >
                        {option.name}
                      </a>
                    );
                  }
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderBuyBox() {
    if (!this.shopUrl || this.product.uvex_b2b_shop_button_hide) {
      return;
    }

    return (
      <div class="product-buybox">
        <a href={this.shopUrl + this.orderNumber} class="btn btn-primary btn-block" target="_blank">
          {translate('sw.detail.buybox.buyOnline')}
        </a>
      </div>
    );
  }

  renderDealerLocator() {
    if (!this.product.links.dealerlocator) {
      return;
    }

    return (
      <div class="product-buybox">
        <a href={this.product.links.dealerlocator} class="btn btn-primary btn-block" target="_blank">
          {translate('sw.detail.dealerlocator.button')}
        </a>
      </div>
    );
  }

  renderCommerceConnector() {
    if (this.ccShowWidget !== 1 || !this.ccToken) {
      return;
    }

    return (
      <uvex-commerce-connector
        token={this.ccToken}
        widgetid={this.ccWidgetid}
        locale={this.ccLocale}
        displaylanguage={this.ccDisplayLanguage}
        subid={this.ccSubid}
        allowedCountries={this.ccAllowedCountries}
        orderNumber={this.orderNumber}
      />
    );
  }

  renderTabs() {
    if (
      this.variant.properties.length < 1 &&
      this.variant.assets.length < 1 &&
      !this.variant.hasCE &&
      this.variant.description === '' &&
      this.variant.videos.length < 1 &&
      this.variant.tabs.length < 1
    ) {
      return;
    }

    return (
      <div class="container m-t-3">
        <div class="row">
          <div class="col-sm-12">
            <uvex-tabs-accordion>
              <uvex-tabs>
                {this.renderTabDescription()}
                {this.renderTabProperties()}
                {this.renderTabDocuments()}
                {this.renderTabVideos()}
                {this.renderAdditionalTabs()}
              </uvex-tabs>
            </uvex-tabs-accordion>
          </div>
        </div>
      </div>
    );
  }

  renderTabDescription() {
    if (this.variant.description === '') {
      return;
    }

    return (
      <uvex-tab label={translate('sw.detail.tabs.description')}>
        <div>
          <div innerHTML={this.variant.description} />
          {this.variant.sustainability.description ? (
            <div>
              <p>
                <strong innerHTML={translate('sw.detail.tabs.description.sustainability')} />
              </p>
              <div innerHTML={this.variant.sustainability.description} />
            </div>
          ) : (
            ''
          )}
        </div>
      </uvex-tab>
    );
  }

  renderTabProperties() {
    if (this.variant.properties.length < 1) {
      return;
    }

    return (
      <uvex-tab label={translate('sw.detail.tabs.properties')}>
        <table class="table-shop unstyled">
          <thead>
            <tr>
              <th>{translate('sw.detail.tabs.properties.attribute')}</th>
              <th>{translate('sw.detail.tabs.properties.value')}</th>
            </tr>
          </thead>
          <tbody>
            {this.variant.properties.map(property => {
              return (
                <tr>
                  <td data-label={translate('sw.detail.tabs.properties.attribute')}>{property.name}</td>
                  <td data-label={translate('sw.detail.tabs.properties.value')}>{property.value}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </uvex-tab>
    );
  }

  renderTabDocuments() {
    if (this.variant.assets.length < 1 && !this.variant.hasCE) {
      return;
    }

    return (
      <uvex-tab label={translate('sw.detail.tabs.documents')}>
        {this.variant.assets.length > 0 ? (
          <ul class="list-angle-double">
            {this.variant.assets.map(asset => {
              return (
                <li>
                  <a href={asset.link} target="_blank">
                    <strong>{translate('sw.detail.tabs.documents.type.' + asset.type)}</strong>
                  </a>
                </li>
              );
            })}
          </ul>
        ) : (
          ''
        )}

        {this.variant.hasCE && this.variant.ean ? (
          <div>
            <h2 class="ce-headline">{translate('sw.detail.tabs.documents.ce')}</h2>
            <ul class="list-angle-double">
              <li>
                <a
                  target="_blank"
                  href={this.ceDeclarationsUrl + '?searchLanguage=' + this.ccDisplayLanguage.substring(0, 2).toUpperCase() + '&q=' + this.variant.ean}
                  class="external"
                >
                  <strong>{translate('sw.detail.tabs.documents.ceLink')}</strong>
                </a>
              </li>
            </ul>
          </div>
        ) : (
          ''
        )}
      </uvex-tab>
    );
  }

  renderTabVideos() {
    if (this.variant.videos.length < 1) {
      return;
    }

    return (
      <uvex-tab label={translate('sw.detail.tabs.videos')}>
        {this.variant.videos.map(video => (
          <uvex-youtube-video src={video} />
        ))}
      </uvex-tab>
    );
  }

  renderAdditionalTabs() {
    if (!this.variant.tabs || this.variant.tabs.length < 1) {
      return;
    }

    return this.variant.tabs.map(tab => {
      return <uvex-tab label={tab.title} innerHTML={tab.content} />;
    });
  }

  renderCo2Wolke() {
    if (!this.variant.sustainability?.balance) {
      return;
    }

    const eStyle = {
      stroke: '#fff',
      strokeMiterlimit: '10',
      strokeWidth: '10px',
    };

    let stringLengthStyle = {};
    switch (String(this.variant.sustainability.balance).replace(',', '').replace('.', '').length) {
      case 3:
        stringLengthStyle['fontSize'] = '110px';
        stringLengthStyle['letterSpacing'] = '-2px';
        break;
      case 4:
        stringLengthStyle['fontSize'] = '100px';
        stringLengthStyle['letterSpacing'] = '-2px';
        break;
      default:
        stringLengthStyle['fontSize'] = '136px';
        break;
    }

    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 637 637" class="co2-wolke co2-wolke-detail">
        <defs>
          <linearGradient id="gradient" gradientTransform="rotate(90)">
            <stop offset="0.7" stop-color="#8098CB" stop-opacity="1" />
            <stop offset="1" stop-color="#8098CB" stop-opacity="0" />
          </linearGradient>
        </defs>
        <ellipse cx="293" cy="341" fill="#8098cb" class="e" style={eStyle} rx="227" ry="240" />
        <path
          fill="#b3c2e0"
          d="M293.3 580.4c-4.4 0-8.9-.1-13.3-.4a283.4 283.4 0 0 1-86.3-101c-21 8.8-40.7 20.3-58.7 34l-6.9-7.2a271.6 271.6 0 0 1 61.3-36 296.1 296.1 0 0 1-27.7-120.9H65.9a256.4 256.4 0 0 1 0-10.2h95.8a298 298 0 0 1 27.6-120.9 273.7 273.7 0 0 1-63.6-37.8c2.2-2.5 4.4-5 6.7-7.3 18.7 14.7 39.2 26.9 61.2 36.1a281.5 281.5 0 0 1 94-106.1h3.6l1.5 2.4 1.7-2.5h3.9A280.1 280.1 0 0 1 392 208.8a263.8 263.8 0 0 0 61.7-36.6l6.7 7.4a274.1 274.1 0 0 1-63.9 38.2c17.1 36.6 27 77.6 27.7 121h96.1V349h-96.1a295 295 0 0 1-27.8 121c22 9.4 42.7 21.6 61.5 36.3a234 234 0 0 1-7 7.3c-18-14-37.8-25.5-59-34.4a282.9 282.9 0 0 1-86.3 100.9c-4.1.2-8.2.3-12.4.3Zm94.3-359a266 266 0 0 1-89.8 17.4v100h116.9a286.5 286.5 0 0 0-27.1-117.4Zm-99.5 17.4c-31.5-.6-61.7-6.6-89.8-17.2a285.6 285.6 0 0 0-27 117.3h116.8Zm-85.2-26.4c26.7 10 55.4 15.6 85.2 16.2V114.4a272 272 0 0 0-85.2 98Zm94.9 16.2a258 258 0 0 0 85.3-16.3 271 271 0 0 0-85.3-97.9Zm0 344.7a273.5 273.5 0 0 0 85.2-97.9 256.9 256.9 0 0 0-85.2-16.3Zm89.7-107A285 285 0 0 0 414.7 349H297.8v100a263 263 0 0 1 89.7 17.3ZM171.4 349c.7 42 10.4 81.8 27.1 117.2a265.2 265.2 0 0 1 89.6-17.2V349ZM203 475.3a272.6 272.6 0 0 0 85.1 98V459.1c-29.8.6-58.4 6.2-85.1 16.2Z"
        />
        <path fill="url(#gradient)" d="M130.9 349H455v160.6a223.4 223.4 0 0 1-126.8 68h-69.8c-49.6-8-94-32.9-127.5-68.8Z" />
        <text text-anchor="middle" x="285" y="466" font-size="70px" fill="#FFF" font-family="UvexNHG" font-weight="700">
          <tspan style={stringLengthStyle}>{this.variant.sustainability.balance}</tspan>
          <tspan letter-spacing="-2">kg</tspan>
        </text>
        <ellipse cx="293" cy="341" fill="none" class="e" style={eStyle} rx="227" ry="240" />
        <path
          fill="#fff"
          d="M293.4 301.3a77.9 77.9 0 0 1-53.1-21.4 73.3 73.3 0 0 1-22.1-51.9 73 73 0 0 1 50.7-69.2 61.3 61.3 0 0 1 17.8-51 64.5 64.5 0 0 1 44.3-18c12.7 0 22.5 3.3 32.2 9a95.8 95.8 0 0 1 84.7-49.4 98.3 98.3 0 0 1 67.9 27.4 93 93 0 0 1 27.4 77.6c21 15.3 34.1 38.9 34.1 65.5 0 22.2-9.6 43-24.5 57.6a86.4 86.4 0 0 1-59.1 23.8H293.4Z"
        />
        <path
          fill="#1B398F"
          d="M501.5 239.1c10-7.1 18.2-9.5 18.2-16.2 0-4.1-2.4-7-7.1-7-5.4 0-8.6 3.8-8.6 9.7h-15c0-14 9.9-21.7 24-21.7s22.2 8.3 22.2 18c0 8.1-3.9 12.1-9 16.1-5.8 4.6-13.8 7.7-17.5 11.6h26.6V262h-47c.1-11.1 5.9-17.6 13.2-22.9Zm-42.3-36.9c0-17.6-7.9-31.2-24-31.2s-24.3 13.8-24.3 31.2 8.2 30.8 24.3 30.8c15.9 0 24-13.2 24-30.8Zm-70.1 0c0-27.8 17.8-48.4 45.9-48.4 28.1 0 45.8 20.7 45.8 48.4s-17.7 48.3-45.8 48.3c-28.1 0-45.9-20.5-45.9-48.3Zm-93.2 0c0-27.7 17.8-48.4 45.5-48.4 10.2 0 19 2.5 25.4 7.2a35.8 35.8 0 0 1 14.3 25.7h-21.2c-1.2-8.7-8.2-15.2-18.7-15.2-15.8 0-23.7 13-23.7 30.7s9.2 30.6 23.8 30.6c11.3 0 18.3-7.4 19.3-17h21.3a37 37 0 0 1-11.2 24.1c-6.9 6.9-16.4 10.5-29.4 10.5-27.1 0-45.4-20.5-45.4-48.2Zm197.8 90.7c21 .2 39.3-8 53.2-21.4a70.6 70.6 0 0 0 22-51.6 70.2 70.2 0 0 0-32.6-60l-2.3-1.6.5-2.7A82.5 82.5 0 0 0 510 82.8a87.7 87.7 0 0 0-62.1-25 85.8 85.8 0 0 0-79.3 48.7l-2.1 4.5-4.1-2.8c-9.3-6.7-20-9.9-31.4-9.9a53.9 53.9 0 0 0-54.3 52.7c0 3.3.4 6.5 1 9.9l.8 3.8-3.8 1a64.6 64.6 0 0 0-48 62.3c0 18.2 7.2 33.8 19.5 45.8a65.8 65.8 0 0 0 47.2 19Z"
        />
      </svg>
    );
  }

  render() {
    return (
      <Host class="container">
        <div class="product-details-container">
          {this.renderImages()}
          <uvex-zoom-window></uvex-zoom-window>
          <header class="product-details-header">
            <h1>{this.product.name}</h1>
          </header>
          {this.renderDetails()}
        </div>
        {this.renderTabs()}
      </Host>
    );
  }
}
